import React, { useEffect } from 'react';
import { NextPage } from 'next';
import Head from 'next/head';
import { fetchBlog, fetchEcotourism, fetchLate, fetchNearTehran, fetchNorth, fetchSouth, fetchStatistics, fetchTehran } from '@/components/home/_helpers/fetchData';
import { blogCarouselType, housesStatistics } from '@/components/home/type';
import { ProductListRecord } from '@/components/reusable/productCard/types';
import Hero from '@/components/home/hero/Hero';
import Header from '@/components/layout/header/Header';
import ServerData from '@/components/home/serverData/ServerData';
import LazyLoadedComponents from '@/components/home/lazyLoadComponents/LazyLoadedComponents';
import Categories from '@/components/home/categories/Categories';
import MostVisited from '@/components/home/mostVisited/MostVisited';
import TicketBanner from '@/components/home/ticketBanner/TicketBanner';
import { useIsWhiteLabel } from '@/components/app/providers/whiteLabelProvider/WhiteLabelProvider';
import NavigationBar from '@/components/layout/NavigationBar/NavigationBar';
import { fetchApi } from '@/components/search/_helpers/fetchApi';
import dynamic from 'next/dynamic';
const title = 'شب | اجاره ویلا، سوئیت و اقامتگاه در شمال و سراسر ایران | امن و متنوع';
const whiteLabelTitle = 'اجاره ویلا و سوئیت در شمال و... | 100% تمیز و امن';
const image = `https://www.shab.ir/images/home/hero/hero.webp`;
const href = `https://www.shab.ir/`;
type Props = {
  lateNight: ProductListRecord[];
  nearTehran: ProductListRecord[];
  tehran: ProductListRecord[];
  north: ProductListRecord[];
  south: ProductListRecord[];
  ecotourism: ProductListRecord[];
  blog: blogCarouselType[];
  statistics: housesStatistics;
  hero: {
    mobile: string;
    desktop: string;
  };
};
const WhiteLabelBanner = dynamic(() => import('@/components/home/whiteLabelBanner/WhiteLabelBanner'), {
  ssr: false
});
// const Categories = dynamic(
//   () => import('@/components/home/categories/Categories'),
//   {
//     ssr: false,
//     loading: CategoriesSkeleton,
//   },
// )
// const MostVisited = dynamic(
//   () => import('@/components/home/mostVisited/MostVisited'),
//   {
//     ssr: false,
//     loading: MostVisitedSkeleton,
//   },
// )

// const NavigationBar = dynamic(
//   () => import('@/components/layout/NavigationBar/NavigationBar'),
//   {
//     ssr: true,
//   },
// )

const HomePage: NextPage<Props> = props => {
  const isWhiteLabel = useIsWhiteLabel();
  const homeDescription = `اجاره ویلا، سوئیت و خانه مبله | اجاره ویلا شمال و سراسر ایران | ${props.statistics.housesCount} اقامتگاه تمیز و امن | رزرو اقامتگاه با بیمه رایگان | پشتیبانی 24 ساعته از شب`;
  const whiteLabelDescription = `اجاره روزانه ویلا، سوئیت و خانه مبله | شمال و سراسر ایران | ${props.statistics.housesCount} اقامتگاه تمیز و امن | بیمه رایگان سفر و اقامت | پشتیبانی 24‌ساعت `;
  const homeTitle = isWhiteLabel ? whiteLabelTitle : title;
  const description = isWhiteLabel ? whiteLabelDescription : homeDescription;
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="index.tsx">
        <meta charSet="utf-8" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <title>{homeTitle}</title>
        <meta name="description" content={description} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta property="og:title" content={homeTitle} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta property="og:description" content={description} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta property="og:url" content={isWhiteLabel ? '/' : href} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta property="og:site_name" content={isWhiteLabel ? '' : 'سایت شب'} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta property="og:type" content="website" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta property="og:image" content={image} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta name="twitter:card" content="summary_large_image" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta name="twitter:site" content="@shabhost" data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta name="twitter:title" content={homeTitle} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta name="twitter:description" content={description} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        <meta name="twitter:image:src" content={image} data-sentry-element="meta" data-sentry-source-file="index.tsx" />
        {!isWhiteLabel && <link rel="canonical" href={href} />}
        <style>
          {'.home-first-section,.home-first-section .footer-wrapper{border-bottom-left-radius:20px;border-bottom-right-radius:20px}.home-first-section{display:flex;flex-direction:column;align-items:center;justify-content:center;height:70vh;position:relative}.home-first-section .content-wrapper{height:fit-content}.home-first-section .content-wrapper .content-header .searchbar-title{display:block;text-align:center;font-weight:700;text-shadow:0 0 20px #000}.home-first-section .content-wrapper .content-header .searchbar-desc{display:block;text-align:center;font-weight:400;text-shadow:0 0 8px #000}.home-first-section .content-wrapper .content-categories{margin-top:45px;padding:0 15px}.home-first-section .content-wrapper .content-categories .category-item{border-radius:40px;transition:.2s linear;margin-right:6px;margin-left:6px}.home-first-section .content-wrapper .content-categories .category-item a{color:#fff;width:100%;height:100%;border-radius:40px;padding:5px 12px;display:flex;cursor:pointer}.home-first-section .footer-wrapper{height:56px;background:linear-gradient(360deg,#000 -7.89%,rgba(0,0,0,0) 108.77%);border-top:1px solid}.hero-section,.hero-section .overlay{position:absolute;top:0;right:0;width:100%;height:100%}.hero-section{z-index:-1;overflow:hidden;border-radius:0 0 24px 24px}.hero-section .overlay{left:0;bottom:0;background:linear-gradient(180deg,rgba(0,0,0,.2),transparent 30%)}.hero-section .desktop-hero{display:block}.hero-section .mobile-hero{display:none}@media only screen and (max-width:991px){.home-first-section{height:373px}.home-first-section .content-wrapper{margin-top:150px}}@media only screen and (max-width:786px){.home-first-section{height:202px}.hero-section{border-radius:0}.content-wrapper hr,.hero-section .desktop-hero,.home-first-section .content-wrapper .content-header .searchbar-title{display:none}.hero-section .mobile-hero{display:block}.content-wrapper{width:100%}.home-first-section .content-wrapper .content-header .searchbar-desc{margin-top:16px;font-weight:700}}'}
        </style>
        {!isWhiteLabel && <>
            <script defer type="application/ld+json" dangerouslySetInnerHTML={{
          __html: `
            {
              "@context": "http://schema.org",
              "@type": "Organization",
              "name": "سایت شب | اجاره ویلا و سوئیت در سراسر ایران",
              "url": "https://www.shab.ir",
              "logo": "https://api.shab.ir/img/logo.png",
              "sameAs": [
                "https://telegram.me/shab_ir",
                "https://twitter.com/shabhost",
                "https://www.instagram.com/_shab.ir/",
                "https://www.linkedin.com/company/shab.ir"
              ],
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "telephone": "+98-21-28111288",
                  "contactType": "customer service",
                  "areaServed": [
                    "تهران", "کردان", "رامسر", "شیراز", "مشهد", "کیش", "اصفهان", "ماسال", "رشت", "قشم",
                    "شهریار", "انزلی", "چالوس", "بابلسر", "متل قو", "یزد", "کاشان", "محمودآباد", "باغ بهادران",
                    "نوشهر", "کلاردشت", "لواسان", "دماوند", "نمک آبرود", "کرج", "تبریز", "آمل", "لاهیجان",
                    "همدان", "سرعین", "گرگان", "ساری", "طالقان", "چادگان", "نور", "ماسوله", "بندرعباس",
                    "فشم", "سوادکوه", "قم", "فومن", "آستارا", "تالش", "بوشهر", "سرخرود", "جواهرده", "ارومیه",
                    "کرمان", "تنکابن", "اردبیل", "ایران"
                  ]
                }
              ]
            }
          `
        }} />
            <script defer type="application/ld+json" dangerouslySetInnerHTML={{
          __html: `
            {
              "@context":"https://schema.org",
              "@type":"WebSite",
              "name":"شب",
              "alternateName":"shab",
              "url":"https://www.shab.ir",
              "potentialAction":{
                "@type":"SearchAction",
                "target":"https://www.shab.ir/search?{search_term_string}",
                "query-input":"required name=search_term_string"
              }
            }
          `
        }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: `
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "Shab.ir",
              "image": "https://api.shab.ir/img/logo.png",
              "@id": "https://www.shab.ir",
              "url": "https://www.shab.ir",
              "telephone": "+982128111288",
              "priceRange": "$$",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "تهران، خیابان شریعتی، بالاتر از پل رومی، کوچه پروین، پلاک 13، واحد 2",
                "addressLocality": "تهران",
                "postalCode": "1933913650",
                "addressCountry": "IR"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 35.7915952,
                "longitude": 51.435121
              },
              "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday"
                ],
                "opens": "09:00",
                "closes": "01:00"
              },
              "sameAs": [
                "https://www.instagram.com/_shab.ir/",
                "https://www.linkedin.com/company/shab.ir",
                "https://twitter.com/shabhost",
                "https://www.shab.ir"
              ] 
            }
          `
        }}></script>
          </>}
      </Head>
      <WhiteLabelBanner data-sentry-element="WhiteLabelBanner" data-sentry-source-file="index.tsx" />
      <Header defaultLightMode={false} data-sentry-element="Header" data-sentry-source-file="index.tsx" />
      {!isWhiteLabel && <ServerData props={props} />}
      <Hero data-sentry-element="Hero" data-sentry-source-file="index.tsx" />
      <Categories data-sentry-element="Categories" data-sentry-source-file="index.tsx" />
      {/*<BannerCarousel>*/}
      {/*  <SnappPayBanner />*/}
      {/*</BannerCarousel>*/}
      {/* {!isWhiteLabel && <TicketBanner />} */}
      <MostVisited data-sentry-element="MostVisited" data-sentry-source-file="index.tsx" />
      <LazyLoadedComponents props={props} data-sentry-element="LazyLoadedComponents" data-sentry-source-file="index.tsx" />
      <NavigationBar data-sentry-element="NavigationBar" data-sentry-source-file="index.tsx" />
    </>;
};
export const getStaticProps = async () => {
  if (process.env.OFFLINE_MODE === 'true') {
    const {
      readHomeSnapshot
    } = await import('@/helpers/offline/snapshot');
    return {
      props: readHomeSnapshot() as Props,
      revalidate: 600
    };
  }
  const [lateNight, nearTehran, north, south, tehran, ecotourism, blog, statistics] = await Promise.all([fetchLate(), fetchNearTehran(), fetchNorth(), fetchSouth(), fetchTehran(), fetchEcotourism(), fetchBlog().then(res => res || []), fetchStatistics()]);
  return {
    props: {
      lateNight,
      nearTehran,
      tehran,
      north,
      south,
      ecotourism,
      blog,
      statistics
    },
    revalidate: 600 // In seconds
  };
};
export default HomePage;